import { Box, Icon, Text } from '@nimbus-ds/components';
import {
  ChevronLeftIcon,
  InvoiceIcon,
  CalculatorIcon,
  StepsIcon,
  CreditCardIcon,
} from '@nimbus-ds/icons';
import { Menu } from '@nimbus-ds/patterns';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Country } from '@tiendanube/common/src/enums';
import ShowByRole from 'App/components/ShowByRole';
import { useNavegate, useHandleMenuClick } from 'App/hooks';
import useShouldRedirectPlanPage from 'App/hooks/useShouldRedirectPlanPage';
import {
  useAdminLinks,
  useGetCountry,
  useGetPlan,
  useIsMobileDevice,
  useLoggedAdminLink,
} from 'domains/Auth/hooks';
import { BILLING_ROUTES } from 'domains/Billing/billingRoutes';
import { CHECKOUT_ROUTES } from 'domains/Billing/Checkout/checkoutRoutes';
import { INVOICES_ROUTES } from 'domains/Billing/Invoices';
import { TRANSACTION_FEES_ROUTES } from 'domains/Billing/TransactionFees/transactionFeesRoutes';
import { useMenuScrollShadow, useSectionActive } from '../../hooks';
import { PATH } from '../utils';

interface BillingMenuProps {
  visible: boolean;
}

const invalidCountriesFortransactionCostsMenu = [Country.CL, Country.CO];

function BillingMenu({ visible }: Readonly<BillingMenuProps>) {
  const { t } = useTranslation('common');
  const { goTo } = useNavegate();
  const menuBodyRef = useMenuScrollShadow();
  const { getOldAdminLink } = useAdminLinks();
  const isMobile = useIsMobileDevice();
  const country = useGetCountry();
  const { hasTransactionFeesForMx } = useGetPlan();
  const plansPageNewAdmin = useShouldRedirectPlanPage();
  const { goToLoggedAdminLink } = useLoggedAdminLink();
  const isSectionActive = useSectionActive();

  const { handleClickItem } = useHandleMenuClick();

  const handleGoToPlansWeb = async () => {
    await goToLoggedAdminLink(PATH.plans);
  };

  return (
    <div
      className={classNames('stratus--menu-container', 'stratus--submenu', {
        visible: visible,
      })}
    >
      <Menu>
        <Menu.Header>
          <Box
            display="flex"
            gap="1"
            as="button"
            backgroundColor="transparent"
            borderColor="transparent"
            onClick={() => goTo('/')}
            alignItems="center"
            cursor="pointer"
          >
            <Icon
              color="neutral-textHigh"
              source={<ChevronLeftIcon size={14} />}
            />
            <Text fontWeight="medium" lineClamp={1} color="neutral-textHigh">
              {t('accountMenu.accountDetails.plansAndSubscriptions')}
            </Text>
          </Box>
        </Menu.Header>
        <div className="stratus--menu-body" ref={menuBodyRef}>
          <Menu.Body>
            <>
              <ShowByRole includeRoles={['invoice_history']}>
                <>
                  <Menu.Button
                    as={Link}
                    to={CHECKOUT_ROUTES.plansAndSubscriptions}
                    active={
                      isSectionActive(CHECKOUT_ROUTES.plansAndSubscriptions) ||
                      isSectionActive(BILLING_ROUTES.coupons)
                    }
                    label={t('accountMenu.payments.title')}
                    onClick={handleClickItem}
                    startIcon={CreditCardIcon}
                  />
                  {isMobile || !plansPageNewAdmin ? (
                    <Menu.Button
                      label={t('accountMenu.plans')}
                      onClick={handleGoToPlansWeb}
                      startIcon={StepsIcon}
                    />
                  ) : (
                    <Menu.Button
                      as={Link}
                      to={BILLING_ROUTES.plans}
                      active={isSectionActive(BILLING_ROUTES.plans)}
                      label={t('accountMenu.plans')}
                      onClick={handleClickItem}
                      startIcon={StepsIcon}
                    />
                  )}
                </>
              </ShowByRole>

              <ShowByRole includeRoles={['invoice_history']}>
                <Menu.Button
                  as="a"
                  href={getOldAdminLink(PATH.invoiceHistory)}
                  active={isSectionActive(INVOICES_ROUTES.invoicesList)}
                  label={t('settingsMenu.general.invoiceHistory')}
                  onClick={handleClickItem}
                  startIcon={InvoiceIcon}
                />
              </ShowByRole>
              <ShowByRole includeRoles={['payments_and_subscriptions']}>
                {!isMobile &&
                  !invalidCountriesFortransactionCostsMenu.includes(country) &&
                  (country !== Country.MX || hasTransactionFeesForMx) && (
                    <Menu.Button
                      as={Link}
                      to={TRANSACTION_FEES_ROUTES.transactionFees}
                      active={isSectionActive(
                        TRANSACTION_FEES_ROUTES.transactionFees,
                      )}
                      label={t('accountMenu.payments.transactionCosts')}
                      onClick={handleClickItem}
                      startIcon={CalculatorIcon}
                    />
                  )}
              </ShowByRole>
            </>
          </Menu.Body>
        </div>
      </Menu>
    </div>
  );
}

export default BillingMenu;
